import type { AllowanceDetailsEnforcementMode } from '@adminhub/graphql-types';

import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackActionSubject } from '../new-analytics-types';

export const enum UsageLimitStatus {
  NEARING_LIMIT = 'NEARING_LIMIT',
  REACHED_LIMIT = 'REACHED_LIMIT',
  OK = 'OK',
}

export const enum ExportMeterUsageDetailsSource {
  EXPORT_METER_USAGE_DETAILS_MODAL = 'exportMeterUsageDetailsModal',
  EXPORT_METER_USAGE_DETAILS_REQUEST_FAILED_FLAG = 'exportMeterUsageDetailsRequestFailedFlag',
}

type ScreenKeys = 'meterUsageLandingScreen' | 'meterUsageDetailsScreen' | 'exportMeterUsageDetailsModalScreen';

type UIEventKeys =
  | 'meterRowClick'
  | 'landingPageLearnMoreClick'
  | 'detailsPageLearnMoreClick'
  | 'viewUsageClick'
  | 'meterDetailsTabClick'
  | 'userActionClick'
  | 'learnMoreAboutCreditsClick'
  | 'pageFilterSelectedOption'
  | 'usersTableHeaderPopupClick'
  | 'getRovoDevStandardClick'
  | 'overageControlToggled'
  | 'startRovoDevStandardTrialClick'
  | 'setOverageLimitClick'
  | 'saveOverageLimitClick'
  | 'cancelOverageLimitClick'
  | 'confirmUpdateOverageLimitClick'
  | 'cancelUpdateOverageLimitClick'
  | 'editOverageLimitClick'
  | 'removeOverageLimitClick'
  | 'confirmRemoveOverageLimitClick'
  | 'cancelRemoveOverageLimitClick'
  | 'confirmDisableExtraUsageClick'
  | 'cancelDisableExtraUsageClick'
  | 'confirmEnableExtraUsageClick'
  | 'cancelEnableExtraUsageClick'
  | 'setExtraUsageNudgeDismissClick'
  | 'setExtraUsageNudgeViewCreditSettingsClick'
  | 'exportMeterUsageDetailsButtonClick'
  | 'exportMeterUsageDetailsModalExportButtonClick'
  | 'exportMeterUsageDetailsContactSupportLinkClick'
  | 'appUsageTabManageAppButtonClick';

type TrackEventKeys = 'overageLimit' | 'exportMeterUsageDetailsExportRequestFinished';

const meterUsageLanding = 'meterUsageLanding';
export const meterUsageDetails = 'meterUsageDetails';

export const usageMonitoringEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: ['https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/5561108092/CBP+Analytics+Spec'],
  screen: {
    meterUsageLandingScreen: (): ScreenEvent => ({
      data: {
        name: meterUsageLanding,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86429',
    }),
    meterUsageDetailsScreen: (attributes: {
      meterType: string;
      isMeterUsageDetailsExportEnabled: boolean;
      tabName?: string;
    }): ScreenEvent => ({
      data: {
        name: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86435',
    }),
    exportMeterUsageDetailsModalScreen: (attributes: { meterName: string }): ScreenEvent => ({
      data: {
        name: 'exportMeterUsageDetailsModal',
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91954',
    }),
  },
  ui: {
    meterRowClick: (attributes: { meterType: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Row,
        actionSubjectId: 'meterRow',
        source: meterUsageLanding,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86436',
    }),
    landingPageLearnMoreClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'learnMore',
        source: meterUsageLanding,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/16182',
    }),
    detailsPageLearnMoreClick: (attributes: { meterType: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'learnMore',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/16182',
    }),
    viewUsageClick: (attributes: { meterType: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewUsage',
        source: meterUsageLanding,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89129',
    }),
    meterDetailsTabClick: (attributes: { tabName: string; meterId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Tab,
        actionSubjectId: 'meterDetailsTab',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89130',
    }),
    userActionClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'userAction',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89131',
    }),
    learnMoreAboutCreditsClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'learnMoreAboutCredits',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89132',
    }),
    pageFilterSelectedOption: (attributes: { filterName?: string; filterType: string }): UIEvent => ({
      data: {
        action: Action.Selected,
        actionSubject: ActionSubject.Dropdown,
        actionSubjectId: 'pageFilter',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89598',
    }),
    usersTableHeaderPopupClick: (attributes: { headerName: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'usersTableHeaderPopup',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89597',
    }),
    getRovoDevStandardClick: (attributes: { status: UsageLimitStatus }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'getRovoDevStandard',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89667',
    }),
    overageControlToggled: (): UIEvent => ({
      data: {
        action: Action.Toggled,
        actionSubject: ActionSubject.Toggle,
        actionSubjectId: 'overageControl',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89599',
    }),
    startRovoDevStandardTrialClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'startRovoDevStandardTrial',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89670',
    }),
    setOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'setOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89600',
    }),
    saveOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'saveOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89601',
    }),
    cancelOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89602',
    }),
    confirmUpdateOverageLimitClick: (attributes: { enforcementMode: AllowanceDetailsEnforcementMode; overageCap?: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmUpdateOverageLimit',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89675',
    }),
    cancelUpdateOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelUpdateOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89604',
    }),
    editOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89605',
    }),
    removeOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89606',
    }),
    confirmRemoveOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmRemoveOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89608',
    }),
    cancelRemoveOverageLimitClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelRemoveOverageLimit',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89609',
    }),
    confirmDisableExtraUsageClick: (attributes: { enforcementMode: AllowanceDetailsEnforcementMode; overageCap?: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmDisableExtraUsage',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89610',
    }),
    cancelDisableExtraUsageClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelDisableExtraUsage',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89611',
    }),
    confirmEnableExtraUsageClick: (attributes: { enforcementMode: AllowanceDetailsEnforcementMode; overageCap?: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmEnableExtraUsage',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89612',
    }),
    cancelEnableExtraUsageClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelEnableExtraUsage',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89613',
    }),
    setExtraUsageNudgeDismissClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'setExtraUsageNudgeDismiss',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89668',
    }),
    setExtraUsageNudgeViewCreditSettingsClick: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'setExtraUsageNudgeViewCreditSettings',
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89669',
    }),
    exportMeterUsageDetailsModalExportButtonClick: (attributes: { meterName: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'exportMeterUsageDetailsModalExportButton',
        attributes,
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91955',
    }),
    exportMeterUsageDetailsContactSupportLinkClick: (attributes: {
      meterName: string;
      source: ExportMeterUsageDetailsSource;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'exportMeterUsageDetailsContactSupportLink',
        attributes: {
          meterName: attributes.meterName,
        },
        source: attributes.source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91956',
    }),
    exportMeterUsageDetailsButtonClick: (attributes: { meterName: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'exportMeterUsageDetailsButton',
        attributes,
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92039',
    }),
    appUsageTabManageAppButtonClick: (attributes: { appName: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'appUsageTabManageAppButton',
        attributes,
        source: meterUsageDetails,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94228',
    }),
  },
  track: {
    overageLimit: (attributes: {
      enforcementMode?: AllowanceDetailsEnforcementMode;
      overageCap?: number;
      ceiling: number;
      balance: number;
    }): TrackEvent => ({
      data: {
        action: Action.Updated,
        actionSubject: TrackActionSubject.OverageLimit,
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/89614',
    }),
    exportMeterUsageDetailsExportRequestFinished: (attributes: { success: boolean; meterName: string }): TrackEvent => ({
      data: {
        action: Action.Fetch,
        actionSubject: TrackActionSubject.ExportMeterUsageDetails,
        actionSubjectId: 'exportMeterUsageDetailsExportRequestFinished',
        source: meterUsageDetails,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92108',
    }),
  },
  operational: {},
};
